import React, { useState } from 'react';
import { Share2, PenLine } from 'lucide-react';
import ShareContentModal from '../../../components/ShareContentModal';

interface PerspectivesHeaderProps {
  category: string;
  publishedAt: string;
  title: string;
  summary: string;
  onWriteResponse?: () => void;
}

const CATEGORY_STYLES: Record<string, { color: string; bg: string }> = {
  'AI & Labor': { color: '#1d4ed8', bg: '#dbeafe' },
  'Immigration Policy': { color: '#b45309', bg: '#fef3c7' },
  Education: { color: '#059669', bg: '#d1fae5' },
  'Climate & Energy': { color: '#0891b2', bg: '#cffafe' },
  Politics: { color: '#7c3aed', bg: '#ede9fe' },
  Economy: { color: '#dc2626', bg: '#fef2f2' },
  Technology: { color: '#2563eb', bg: '#dbeafe' },
  Health: { color: '#059669', bg: '#d1fae5' }
};

const PerspectivesHeader: React.FC<PerspectivesHeaderProps> = ({
  category,
  publishedAt,
  title,
  summary,
  onWriteResponse
}) => {
  const catStyle = CATEGORY_STYLES[category] || { color: '#64748b', bg: '#f1f5f9' };
  const dateStr = publishedAt
    ? new Date(publishedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

  const [showShareModal, setShowShareModal] = useState(false);

  return (
    <>
      <div className="bg-white px-4 md:px-0 pt-7 pb-0">
        <div className="max-w-[812px] mx-auto">
          {/* Category + Date + Actions */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded"
              style={{
                background: catStyle.bg,
                color: catStyle.color,
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {category}
            </span>
            <span className="text-[13px] text-slate-400">{dateStr}</span>
            <div className="flex-1" />
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 text-[13px] cursor-pointer hover:bg-slate-50 transition"
            >
              <Share2 size={13} /> Share
            </button>
            {onWriteResponse && (
              <button
                onClick={onWriteResponse}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-none bg-growth-green text-white text-[13px] font-semibold cursor-pointer hover:opacity-90 transition"
              >
                <PenLine size={13} /> Write a Response
              </button>
            )}
          </div>

          {/* Title + Summary */}
          <h2
            className="text-2xl font-extrabold text-slate-900 leading-tight mb-1.5"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            {title}
          </h2>
          <p
            className="text-[15px] text-slate-500 leading-relaxed mb-6"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            {summary}
          </p>
        </div>
      </div>

      {showShareModal && (
        <ShareContentModal
          isOpen
          title="Share Perspectives"
          smallTitle="Share this Topic!"
          shareUrl={window.location.href}
          titleBtn="Done"
          onClick={() => setShowShareModal(false)}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
};

export default PerspectivesHeader;
